import { useRef, useState, useCallback } from "react";

// Segmented recorder: records ~7s webm segments so each is independently decodable,
// then hands each finished segment to onSegment for transcription.
export function useSegmentRecorder(onSegment, segmentMs = 7000) {
  const [recording, setRecording] = useState(false);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const timerRef = useRef(null);
  const activeRef = useRef(false);

  const startSegment = useCallback(() => {
    if (!streamRef.current) return;
    const mr = new MediaRecorder(streamRef.current, { mimeType: "audio/webm" });
    const chunks = [];
    mr.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
    mr.onstop = () => {
      const blob = new Blob(chunks, { type: "audio/webm" });
      if (blob.size > 6000) onSegment(blob);
      if (activeRef.current) startSegment();
    };
    recRef.current = mr;
    mr.start();
    timerRef.current = setTimeout(() => {
      if (mr.state === "recording") mr.stop();
    }, segmentMs);
  }, [onSegment, segmentMs]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      activeRef.current = true;
      setRecording(true);
      startSegment();
      return true;
    } catch (e) {
      return false;
    }
  }, [startSegment]);

  const stop = useCallback(() => {
    activeRef.current = false;
    setRecording(false);
    clearTimeout(timerRef.current);
    if (recRef.current && recRef.current.state === "recording") recRef.current.stop();
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  return { recording, start, stop };
}
